import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash } from "@phosphor-icons/react";

// Recipients / Groups panel — extra WhatsApp/Telegram destinations, per-
// provider help text, and inline "Test" buttons per row.
export default function WARecipientsPanel({
  form, setField,
  previewCount,
  newRecipientName, setNewRecipientName,
  newRecipientNumber, setNewRecipientNumber,
  addRecipient, removeRecipient,
  recipientResults, testingRecipient, testRecipient,
}) {
  return (
    <section className="border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">Penerima</div>
          <div className="text-xs text-muted-foreground mt-1">
            {form.provider === "telegram"
              ? "Isi Chat ID Telegram (individu atau grup, mis. -1001234567890). Nomor petugas tidak otomatis dikirimi via Telegram."
              : "Nomor dari akun petugas (di Master User) otomatis ikut. Tambahkan nomor grup / broadcast di bawah."}
          </div>
        </div>
        {form.provider !== "telegram" && (
          <div className="flex items-center gap-2 text-xs">
            <Switch
              checked={form.notify_petugas}
              onCheckedChange={(v) => setField("notify_petugas", v)}
              data-testid="wa-notify-petugas"
            />
            <span>Kirim ke semua petugas dengan No WA</span>
          </div>
        )}
      </div>

      {/* Group ID help — provider-specific */}
      <div className="border border-dashed border-primary/40 bg-primary/5 p-3 text-xs space-y-1" data-testid="group-help-panel">
        <div className="overline text-primary">Kirim ke Grup?</div>
        {form.provider === "fonnte" && (
          <>
            <p><strong>Fonnte:</strong> Isi kolom Nomor dengan <code className="bg-secondary px-1">Group JID</code> — format <code className="bg-secondary px-1">120363xxxxxxxxxx@g.us</code>.</p>
            <p>Cara ambil JID: buka dashboard Fonnte → menu <strong>Devices → WA Groups</strong>, lalu copy kolom <em>Group ID</em>. Perangkat Fonnte harus sudah bergabung ke grup tersebut.</p>
          </>
        )}
        {form.provider === "twilio" && (
          <>
            <p><strong>Twilio:</strong> WA Business API <span className="text-destructive font-semibold">TIDAK mendukung grup</span> — hanya nomor individu. Baris dengan format <code>@g.us</code> akan otomatis di-skip.</p>
            <p>Untuk kirim ke grup, gunakan provider <strong>Fonnte</strong> atau <strong>Telegram</strong>.</p>
          </>
        )}
        {form.provider === "telegram" && (
          <>
            <p><strong>Telegram:</strong> Isi kolom Nomor dengan <code className="bg-secondary px-1">chat_id grup</code> — biasanya diawali minus, mis. <code className="bg-secondary px-1">-1001234567890</code>.</p>
            <p>Cara ambil chat_id grup: tambahkan bot ke grup → kirim satu pesan → buka <code className="bg-secondary px-1">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> dan cari field <code>chat.id</code>.</p>
          </>
        )}
      </div>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left">
            <tr>
              <th className="p-2 overline">Nama</th>
              <th className="p-2 overline">Nomor / Chat ID</th>
              <th className="p-2 overline w-40">Status Test</th>
              <th className="p-2 overline w-32 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {form.extra_recipients.map((r, idx) => {
              const result = recipientResults[idx];
              const busy = testingRecipient === idx;
              return (
                <tr key={`${r.number}-${idx}`} className="border-t border-border">
                  <td className="p-2">{r.name || <span className="text-muted-foreground">-</span>}</td>
                  <td className="p-2 font-mono text-xs break-all">{r.number}</td>
                  <td className="p-2">
                    {busy ? (
                      <span className="text-xs text-muted-foreground animate-pulse">mengirim…</span>
                    ) : result ? (
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 border ${
                          result.ok
                            ? "border-green-600 text-green-700 bg-green-50"
                            : "border-destructive text-destructive bg-destructive/5"
                        }`}
                        title={result.response}
                        data-testid={`test-recipient-result-${idx}`}
                      >
                        {result.ok ? "✓ TERKIRIM" : "✗ GAGAL"}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">belum ditest</span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-xs"
                        onClick={() => testRecipient(idx, r.number)}
                        disabled={busy}
                        data-testid={`test-recipient-${idx}`}
                        title="Kirim pesan test ke nomor / grup ini"
                      >
                        {busy ? "…" : "Test"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-destructive"
                        onClick={() => removeRecipient(idx)}
                        data-testid={`remove-recipient-${idx}`}
                        title="Hapus penerima"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {form.extra_recipients.length === 0 && (
              <tr><td colSpan={4} className="p-3 text-center text-muted-foreground text-xs">Belum ada penerima tambahan.</td></tr>
            )}
            <tr className="border-t border-border bg-secondary/20">
              <td className="p-2">
                <Input
                  value={newRecipientName}
                  onChange={(e) => setNewRecipientName(e.target.value)}
                  placeholder={form.provider === "telegram" ? "Grup Telegram (opsional)" : "Grup WA Lapangan (opsional)"}
                  className="rounded-xl h-9"
                  data-testid="new-recipient-name"
                />
              </td>
              <td className="p-2">
                <Input
                  value={newRecipientNumber}
                  onChange={(e) => setNewRecipientNumber(e.target.value)}
                  placeholder={
                    form.provider === "telegram"
                      ? "-1001234567890 (chat_id / grup)"
                      : form.provider === "fonnte"
                      ? "08xxxxxxxxxx  atau  120363xxxx@g.us (grup)"
                      : "08xxxxxxxxxx (grup tidak didukung)"
                  }
                  className="rounded-xl h-9 font-mono"
                  data-testid="new-recipient-number"
                />
              </td>
              <td className="p-2" />
              <td className="p-2 text-right">
                <Button
                  type="button"
                  size="sm"
                  onClick={addRecipient}
                  className="rounded-xl"
                  data-testid="add-recipient-button"
                >
                  <Plus size={14} />
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="text-xs text-muted-foreground font-mono">
        Total penerima aktif saat ini: <span className="font-bold text-foreground" data-testid="wa-recipient-count">{previewCount}</span> nomor
      </div>
    </section>
  );
}
